import React, { ReactElement, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import cx from 'classnames';
import { Check, X } from 'lucide-react';

import { Button } from '@ui/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@ui/components/ui/dialog';
import { Input } from '@ui/components/ui/input';
import { Label } from '@ui/components/ui/label';

import type { SentryAccess } from '@data';
import { reasonOf } from '@data/utils/errors';
import { useLinkProductSentryToken } from '@data/hooks/products';

/** Long enough to show the shape without anybody mistaking it for a real one. */
const EXAMPLE_TOKEN = 'sntrys_eyJpYXQiOjE3NTk0MjgwMDAsInVybCI6...';

type Phase = 'form' | 'checking' | 'ok' | 'bad';

/**
 * Fixed per screen, so the box grows and shrinks instead of snapping between three sizes.
 *
 * Measured from the rendered content in a browser. The form is sized to its own content plus the footer,
 * so the field is not left floating 73px above the grey bar; the two short screens keep room around them
 * and centre themselves in it, because a lone tick in a tight box reads as cramped.
 */
const HEIGHTS: Record<Phase, number> = { form: 280, checking: 216, ok: 260, bad: 260 };

type Props = {
  productId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

/**
 * Pasting a personal Sentry token for a product Panenco does not administer.
 *
 * The steps are in the dialog rather than behind a link to a runbook, because somebody hits this while
 * looking at an empty picker and the whole problem is not knowing there is a way through.
 *
 * Three screens: the steps and the field, the wait while Sentry is asked, then what it said. They slide in
 * from the right the way the onboarding dialog's steps do, and the box changes height under them, so the
 * flow reads as one thing moving forward rather than three dialogs replacing each other.
 *
 * The answer replaces the form rather than appearing under it: the token is the only thing on the first
 * screen and whether it worked is the only thing on the last. It is not a toast either, because a token
 * has to be replaced in Sentry rather than retyped, which is not a four-second job.
 */
const SentryTokenDialog = ({ productId, open, onOpenChange }: Props): ReactElement => {
  const { t } = useTranslation();
  const [token, setToken] = useState('');
  const [access, setAccess] = useState<SentryAccess | null>(null);
  const [failure, setFailure] = useState<string | null>(null);
  // False until something moves, so opening the dialog does not replay the slide on the first screen.
  const [hasMoved, setHasMoved] = useState(false);
  // This component is mounted for the life of the section, so its state outlives a close. Sentry can take
  // seconds, and an answer to an attempt somebody walked away from must not open the next one.
  const attempt = useRef(0);
  const link = useLinkProductSentryToken(productId);

  const phaseOf = (): Phase => {
    if (link.isPending) return 'checking';
    if (access) return 'ok';
    if (failure) return 'bad';
    return 'form';
  };
  const phase = phaseOf();

  const close = (): void => {
    attempt.current += 1;
    setToken('');
    setAccess(null);
    setFailure(null);
    setHasMoved(false);
    onOpenChange(false);
  };

  const submit = async (): Promise<void> => {
    const mine = attempt.current;
    setFailure(null);
    setHasMoved(true);
    try {
      const access = await link.mutateAsync(token.trim());
      if (attempt.current === mine) setAccess(access);
    } catch (error) {
      if (attempt.current !== mine) return;
      // The API names the rule it refused; the sentence in `message` is prose that changes.
      const reason = reasonOf(error);
      if (reason === 'sentryTokenRefused') setFailure(t('connectedApps.sentryTokenRefused'));
      else if (reason === 'sentryTokenSeesNothing') setFailure(t('connectedApps.sentryTokenSeesNothing'));
      else if (reason === 'sentryTokenStorageUnavailable') setFailure(t('connectedApps.sentryTokenNoStorage'));
      else setFailure(t('connectedApps.sentryTokenUnreachable'));
    }
  };

  /** The token is kept, so a fixed one is a paste over the top rather than a retype from nothing. */
  const tryAgain = (): void => setFailure(null);

  const outcome = (tone: 'ok' | 'bad', icon: ReactElement, title: string, body: string): ReactElement => (
    // The screen swaps under an unchanged heading, so a reader hears nothing unless the answer says it is
    // one. A refusal is what somebody has to act on, so it interrupts; a success waits its turn.
    <div className="flex flex-col items-center gap-3 text-center" role={tone === 'ok' ? 'status' : 'alert'}>
      {/* One circle with a white mark on it. #a7f3d0 is one step down the scale from #d1fae5, with the red
          a step down to match, so the pair still reads as a pair. White on this measures 1.21:1, so the
          mark is deliberately soft and the sentence under it carries the meaning. */}
      <span
        className={cx(
          'flex size-[72px] items-center justify-center rounded-full text-white duration-300 ease-out animate-in zoom-in-50 motion-reduce:animate-none',
          tone === 'ok' ? 'bg-[#a7f3d0]' : 'bg-[#fecaca]',
        )}
      >
        {icon}
      </span>
      <p className="font-medium text-foreground">{title}</p>
      <p className="max-w-sm text-sm text-muted-foreground">{body}</p>
    </div>
  );

  const body = (): ReactElement => {
    if (phase === 'checking') {
      return (
        <div className="flex flex-col items-center gap-4 pt-4 text-center">
          {/* Three dots on the pulse the Phill mark uses while a report drafts: the app already says
              "waiting" this way. Staggered, so they fade in turn rather than together. */}
          <span className="flex items-center gap-1.5" role="status" aria-label={t('connectedApps.sentryTokenChecking')}>
            {[0, 1, 2].map(index => (
              <span
                key={index}
                className="size-2 animate-pulse rounded-full bg-muted-foreground motion-reduce:animate-none"
                style={{ animationDelay: `${index * 0.18}s` }}
              />
            ))}
          </span>
          <p className="text-sm text-muted-foreground">{t('connectedApps.sentryTokenChecking')}</p>
        </div>
      );
    }

    if (phase === 'ok' && access) {
      return outcome(
        'ok',
        <Check className="size-[52px]" strokeWidth={3} aria-hidden />,
        t('connectedApps.sentryTokenWorkedTitle'),
        t('connectedApps.sentryTokenAccepted', {
          organizations: t('connectedApps.sentryTokenOrganizations', { count: access.organizations }),
          projects: t('connectedApps.sentryTokenProjects', { count: access.projects }),
        }),
      );
    }

    if (phase === 'bad' && failure) {
      return outcome(
        'bad',
        <X className="size-[52px]" strokeWidth={3} aria-hidden />,
        t('connectedApps.sentryTokenFailedTitle'),
        failure,
      );
    }

    return (
      <div className="flex flex-col gap-4">
        {/* Orange numbers: the steps are the one sequence on this screen, and the marker is what carries
            that. `text-primary` rather than a literal, so both themes get their own value. */}
        <ol className="ml-4 flex list-decimal flex-col gap-2 text-sm text-muted-foreground [&>li::marker]:font-medium [&>li::marker]:text-primary">
          <li>{t('connectedApps.sentryTokenStepSignIn')}</li>
          <li>{t('connectedApps.sentryTokenStepFindPage')}</li>
          <li>{t('connectedApps.sentryTokenStepScopes')}</li>
          <li>{t('connectedApps.sentryTokenStepCopy')}</li>
        </ol>

        <div className="flex flex-col gap-1.5">
          <Label htmlFor="sentry-token">{t('connectedApps.sentryTokenLabel')}</Label>
          <Input
            id="sentry-token"
            value={token}
            onChange={event => setToken(event.target.value)}
            placeholder={EXAMPLE_TOKEN}
            autoComplete="off"
            spellCheck={false}
            // No monospace, though a token is the obvious candidate for it: at the same 14px it renders 16%
            // wider than the sans around it, which reads as a bigger field in the middle of the dialog. It
            // would also be the only mono input in the app, and this one is pasted into rather than read.
          />
          <p className="text-xs text-muted-foreground">{t('connectedApps.sentryTokenHelper')}</p>
        </div>
      </div>
    );
  };

  /** Every screen but the wait keeps the footer bar, so the way out is always in the same place. */
  const footer = (): ReactElement | null => {
    if (phase === 'checking') return null;
    if (phase === 'ok') {
      return (
        <Button type="button" onClick={close}>
          {t('actions.close')}
        </Button>
      );
    }
    if (phase === 'bad') {
      return (
        <>
          <Button type="button" variant="outline" onClick={close}>
            {t('actions.cancel')}
          </Button>
          <Button type="button" onClick={tryAgain}>
            {t('connectedApps.sentryTokenTryAgain')}
          </Button>
        </>
      );
    }
    return (
      <>
        <Button type="button" variant="outline" onClick={close}>
          {t('actions.cancel')}
        </Button>
        <Button type="button" onClick={submit} disabled={!token.trim()}>
          {t('connectedApps.sentryTokenContinue')}
        </Button>
      </>
    );
  };

  const footerButtons = footer();

  return (
    <Dialog open={open} onOpenChange={next => (next ? onOpenChange(true) : close())}>
      {/* Wider than the default dialog, like the other pickers' ones: at `lg` every step wrapped onto two
          lines, which reads as dense rather than as four short instructions. Type and padding are the
          dialog's own, so this matches every other dialog in the app. */}
      <DialogContent className="sm:max-w-xl">
        <DialogHeader>
          <DialogTitle>{t('connectedApps.sentryTokenTitle')}</DialogTitle>
          {/* Only while there is something to explain. Once the token has been sent, the screen is about
              what came back. */}
          {phase === 'form' && <DialogDescription>{t('connectedApps.sentryTokenIntro')}</DialogDescription>}
        </DialogHeader>

        <div className="flex flex-col transition-[height] duration-300 ease-out" style={{ height: HEIGHTS[phase] }}>
          {/* Keyed by phase, so each screen mounts fresh and slides in from the right the way the
              onboarding dialog's steps do. The one before it unmounts at once: no cross-fade, so there is
              never a ghost of the previous screen behind this one. */}
          <div
            key={phase}
            className={cx(
              // `min-h-0` so the declared height is what the box is: without it a flex child refuses to
              // shrink below its content and the box grows past the number, which is what made the
              // transition jump.
              // `px-1` rather than `pr-1`: a scroll container clips in both directions, and the field's
              // focus ring sits 3px outside its border, so with no room on the left the ring was cut off
              // down the edge of the box. The 4px each side is what the ring needs.
              'flex min-h-0 flex-1 flex-col overflow-x-hidden overflow-y-auto px-1',
              phase !== 'form' && 'justify-center',
              hasMoved && 'duration-300 ease-out animate-in fade-in-0 slide-in-from-right-6 motion-reduce:animate-none',
            )}
          >
            {body()}
          </div>
          {footerButtons && <DialogFooter className="mt-4">{footerButtons}</DialogFooter>}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default SentryTokenDialog;
